import type {
  RenderBox,
  RenderImageBox,
  RenderObjectBox,
  RenderGroupBox,
  RenderTextBox,
  RenderSmartObjectBox,
  RenderVectorBox,
  RenderAdjustmentBox,
  RenderRect,
} from '@lite-workspace/shared/canvas-engine/core';
import { thumbHashToDataURL } from 'thumbhash';
import {
  browserDrawableOriginal,
  transformEffectOrigins,
  transformGradientOverlay,
  transformStrokeGradient,
  multiplyAffine,
  scaleMaskRect,
} from '@lite-workspace/photobook-model';
import {
  adjustmentResourceIds,
  maskMirrorsWithBox,
  scalePaths,
  type AdjustmentBoxParams,
} from '@lite-workspace/dto-fotoclass-templates';
import { spreadBufferSize } from './camera-transform';
import {
  toTextSceneBox,
  toSceneLayerStyles,
  type SceneLayerStyles,
  type AdapterDeps,
  type SceneShadow,
  type SceneTextBox,
} from './text-scene-adapter';
import { buildZoneShapes, type ZoneShape, type ZonePrintSizes, type ZoneVisibility, type ZoneStyle } from './zone-shapes';
import { frameDecorOf, scaleFrameDecor, withoutLiveDecor, type SceneFrameDecor } from './frame-decor.model';
import { frameStrokeAllowedIn, type AlbumStrokeScope } from './frame-stroke-eligibility';
import {
  bezierPathToClipKnots,
  buildVectorDrawable,
  ellipseSegs,
  roundedRectSegs,
  scaleFill,
  scaleSceneVector,
  shiftSceneVectorFill,
  type SceneClipKnot,
  type SceneVectorBox,
} from './vector-scene-adapter';
import { offsetAffine, scaleAffineTranslation, type Affine } from './text-affine';

// `spread` is the PS percentage of the (already scaled) blur, so it must not scale again.
function scaleShadow(sh: SceneShadow, s: number): SceneShadow {
  return {
    ...sh,
    blur: sh.blur * s,
    dx: sh.dx * s,
    dy: sh.dy * s,
    ...(sh.distance == null ? {} : { distance: sh.distance * s }),
  };
}

export interface SceneImageRef {
  /** Preserve authored PNG samples; other formats keep the ordinary browser decoding path. */
  preservePngRgba?: boolean;
  ref: string;
  url: string;
  thumbUrl?: string;
  placeholderUrl?: string;
  requiresRef?: string;
  /** Paint only once this one is decoded — a full-bleed layer missing for a frame reads as a white flash. */
  blocking?: boolean;
  /** Identity of the PHOTO behind the ref (ImageAlbum.id) — survives box-id renames and rendition (url) changes,
   *  so the pipeline can tell "same photo, different resolution/frame" from "different photo". */
  imageId?: string;
  /** Ladder rung (long-side px) of `url` when it is a rendition rung — feeds the session rung floor on decode. */
  rungPx?: number;
}

export interface SpreadScene {
  sceneJson: string;
  imageRefs: SceneImageRef[];
  binaryRefs?: SceneBinaryRef[];
}

export interface SceneBinaryRef {
  ref: string;
  url?: string;
  data?: string;
  mimeType?: string;
  fileName?: string;
}

function adjustmentIsActive(box: RenderAdjustmentBox): boolean {
  return box.adjustment.enabled !== false && box.opacity !== 0 && box.adjustment.fillOpacity !== 0;
}

export function collectBinaryRefs(boxes: readonly RenderBox[]): SceneBinaryRef[] {
  const refs = new Map<string, SceneBinaryRef>();
  const walk = (box: RenderBox): void => {
    if (box.visible === false) return;
    if (box.type === 'group') {
      box.children.forEach(walk);
      return;
    }

    if (box.type !== 'adjustment' || !adjustmentIsActive(box)) return;
    for (const id of adjustmentResourceIds(box.adjustment)) {
      const resource = box.adjustmentResources?.find(item => item.id === id);
      const ref = `adjustment:${id}`;
      refs.set(ref, {
        ref,
        url: resource?.url,
        data: resource?.data,
        mimeType: resource?.mimeType,
        fileName: resource?.fileName,
      });
    }
  };

  boxes.forEach(walk);
  return [...refs.values()];
}

/** Container-relative photo placement override for a box, applied during a live inline-crop gesture. */
export interface LiveCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
}

/** Live decor-trim window/content override for an object/background/smartObject box, applied during a live trim gesture. */
export interface LiveTrimOverride {
  window: RenderRect;
  content: RenderRect;
}

/**
 * Substitutes live crop/trim gesture overrides INTO the boxes, for the build_scene path: the wire model is a
 * pure RenderBox mirror, so overrides ride the same fields the persisted values use (imageRect / rect+contentRect)
 * instead of extra wire parameters. toSpreadScene keeps applying the same overrides inline during its walk —
 * both paths must resolve them identically or a crop drag renders differently under the flag.
 */
export function applyLiveOverridesToBoxes(
  boxes: readonly RenderBox[],
  liveCropOverrides?: ReadonlyMap<string, LiveCropRect>,
  liveTrimOverrides?: ReadonlyMap<string, LiveTrimOverride>,
): readonly RenderBox[] {
  if (!liveCropOverrides?.size && !liveTrimOverrides?.size) return boxes;
  const map = (box: RenderBox): RenderBox => {
    if (box.type === 'group') {
      const group = box as RenderGroupBox;
      const children = group.children.map(map);
      return children.some((c, i) => c !== group.children[i]) ? { ...group, children } : box;
    }

    if (box.type === 'image') {
      const ir = liveCropOverrides?.get(box.id);
      return ir ? { ...box, imageRect: { ...ir, rotation: ir.rotation ?? 0 } } : box;
    }

    if (box.type === 'object' || box.type === 'background' || box.type === 'smartObject') {
      const trim = liveTrimOverrides?.get(box.id);
      return trim ? { ...box, rect: trim.window, contentRect: trim.content } : box;
    }

    return box;
  };

  return boxes.map(map);
}

interface RustRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

interface RustMaskRect extends RustRect {
  transform?: [number, number, number, number, number, number];
  /** The box's own frame: the renderer mirrors it with the flipped object — see `maskRectOf`. */
  mirrors?: true;
}

interface RustObjectDrawable {
  clipBaseBoxId?: string;
  type: 'object';
  id: string;
  rect: RustRect;
  imageRef: string;
  maskRef?: string;
  clipBaseIds?: string[];
  flipH?: boolean;
  flipV?: boolean;
  blend: string;
  opacity?: number;
  /** Smart-object source affine [a,b,c,d,e,f] (column-major) — present when imageRef is the raw SO source. */
  sourceTransform?: readonly [number, number, number, number, number, number];
  /** Live smart filters (bottom first), only beside `sourceTransform`; Gaussian radius in scene px. */
  smartFilters?: RenderSmartObjectBox['smartFilters'];
  /** Decor trim window: full untrimmed content's placement, a sibling of `rect` in the same frame (not relative to it). */
  contentRect?: RustRect;
  locked?: boolean;
  /** Эффекты слоя, которые движок рисует живыми по его собственной форме (обводка, тени, свечение). */
  decor?: SceneFrameDecor;
  /** Where a clipping mask's pixels lie when that is another layer's rect — see `maskRectOf`. */
  maskRect?: RustMaskRect;
}

interface RustImageDrawable {
  clipBaseBoxId?: string;
  type: 'image';
  id: string;
  rect: RustRect;
  imageRef: string;
  imageRect?: RustRect;
  maskRef?: string;
  /** Where the mask's pixels lie when that is not this box's rect — see `maskRectOf`. */
  maskRect?: RustMaskRect;
  clip?: SceneClipKnot[];
  flipH: boolean;
  flipV: boolean;
  blend: string;
  opacity?: number;
  /** Frame stroke, resolved from the box's own effect or the book default. Both keys travel together or not at all. */
  stroke?: string;
  strokeWidth?: number;
  /** The frame's own layer effects and corner radius; the pair above then carries only the album default. */
  decor?: SceneFrameDecor;
  locked?: boolean;
}

type RustAdjustmentDrawable = AdjustmentBoxParams & {
  type: 'adjustment';
  id: string;
  rect?: RustRect;
  opacity?: number;
  blend?: string;
  maskRef?: string;
  lutRef?: string;
  profileRef?: string;
  documentProfileRef?: string;
  locked?: boolean;
};

type SceneStyleMask = Omit<import('@lite-workspace/dto-fotoclass-templates').LayerStyleMask, 'dataUrl' | 'url' | 'fileId'> & {
  ref?: string;
};
interface SceneStyleFields {
  effectReferencePoint?: { x: number; y: number };
  effectDocumentOrigin?: { x: number; y: number };
  layerStyles?: SceneLayerStyles;
  layerStyleMasks?: { source?: SceneStyleMask[]; post?: SceneStyleMask[] };
  fillOpacity?: number;
  blendInteriorElements?: boolean;
}
type RustDrawable = (
  | RustObjectDrawable
  | RustImageDrawable
  | SceneTextBox
  | ZoneShape
  | SceneVectorBox
  | RustAdjustmentDrawable
) &
  SceneStyleFields;

// The engine refuses the whole scene while a layer-style resource is missing (raster_styles::validate), so these
// decode in the first wave, before the first paint — never in the thumb wave after it.
const STYLE_RESOURCE = { preservePngRgba: true, blocking: true } as const;

function styleMaskRefs(box: RenderBox, addRef: (ref: string, url: string) => void): void {
  if (box.type === 'text' || box.layerStyleRendering === 'live') {
    box.effects?.strokes?.forEach((stroke, index) => {
      if (stroke.fillType !== 'pattern' || stroke.enabled === false || stroke.width <= 0 || (stroke.opacity ?? 1) <= 0) return;
      if (Object.values(stroke.patternPhaseUnits ?? {}).some(phase => phase && phase.value !== 0 && phase.units !== 'Pixels')) {
        throw new Error(`Unsupported stroke pattern phase units: ${box.id}/${index}`);
      }

      const url = stroke.pattern?.url || stroke.pattern?.pixelData;
      if (!url) throw new Error(`Missing active stroke pattern asset: ${box.id}/${index}`);
      addRef(`${box.id}__stroke_pattern_${index}`, url);
    });
  }

  const bevel = box.effects?.bevel;
  const hasBevelSurface =
    bevel?.style !== 'stroke emboss' ||
    box.effects?.strokes?.some(stroke => stroke.enabled !== false && stroke.width > 0 && (stroke.opacity ?? 1) > 0);
  if (
    (box.type === 'text' || box.layerStyleRendering === 'live') &&
    bevel?.enabled !== false &&
    bevel?.useTexture &&
    bevel.size > 0 &&
    hasBevelSurface
  ) {
    const url = bevel.texture?.pattern?.url || bevel.texture?.pattern?.pixelData;
    if (!url) throw new Error(`Missing active bevel texture asset: ${box.id}`);
    addRef(`${box.id}__bevel_texture`, url);
  }

  if (box.layerStyleRendering !== 'live') return;
  const pattern = box.effects?.patternOverlay;
  if (pattern && pattern.enabled !== false && pattern.opacity !== 0 && !(pattern.pattern?.url || pattern.pattern?.pixelData)) {
    throw new Error(`Missing active layer-style pattern asset: ${box.id}`);
  }

  for (const stage of ['source', 'post'] as const)
    box.layerStyleMasks?.[stage]?.forEach((mask, index) => {
      if (mask.disabled || mask.bounds.width === 0 || mask.bounds.height === 0) return;
      const url = mask.url || mask.dataUrl;
      if (!url) throw new Error(`Missing active layer-style mask asset: ${box.id}/${stage}/${index}`);
      addRef(`${box.id}__stylemask_${stage}_${index}`, url);
    });
}

function liveStyleFields(box: RenderBox, offsetX: number, offsetY: number): SceneStyleFields {
  if (box.layerStyleRendering !== 'live') return {};
  const placement = [1, 0, 0, 1, offsetX, offsetY] as const;
  const layerStyles = toSceneLayerStyles({
    ...box,
    effects: {
      ...box.effects,
      ...(box.effects?.colorOverlay && { colorOverlay: { ...box.effects.colorOverlay, rendering: 'live' } }),
    },
  });
  if (layerStyles.strokes) layerStyles.strokes = layerStyles.strokes.map(st => transformStrokeGradient(st, placement));
  if (layerStyles.gradientOverlay) layerStyles.gradientOverlay = transformGradientOverlay(layerStyles.gradientOverlay, placement);
  if (layerStyles.extraGradientOverlays) {
    layerStyles.extraGradientOverlays = layerStyles.extraGradientOverlays.map(g => transformGradientOverlay(g, placement));
  }

  const layerStyleMasks: SceneStyleFields['layerStyleMasks'] = {};
  for (const stage of ['source', 'post'] as const) {
    if (box.layerStyleMasks?.[stage])
      layerStyleMasks[stage] = box.layerStyleMasks[stage].map((mask, index) => {
        const { dataUrl: _data, url: _url, fileId: _file, ...fields } = mask;
        return {
          ...fields,
          ...(mask.disabled || mask.bounds.width === 0 || mask.bounds.height === 0
            ? {}
            : { ref: `${box.id}__stylemask_${stage}_${index}` }),
          transform: [...multiplyAffine(placement, mask.transform)],
        };
      });
  }

  return {
    layerStyles,
    ...transformEffectOrigins(box, placement),
    ...(box.layerStyleMasks && { layerStyleMasks }),
    fillOpacity: box.fillOpacity,
    blendInteriorElements: box.blendInteriorElements,
  };
}

// Live masks stay put in the editor on a flip, so they mirror about the `rect` centre, where trimmed pixels mirror too.
function mirroredStyleMasks(fields: SceneStyleFields, flipH: boolean, flipV: boolean, rect: RustRect): SceneStyleFields {
  const masks = fields.layerStyleMasks;
  if (!(flipH || flipV) || !masks) return fields;
  const angle = (rect.rotation * Math.PI) / 180;
  const [sin, cos] = [Math.sin(angle), Math.cos(angle)];
  const [sin2, cos2] = [2 * sin * cos, cos * cos - sin * sin];
  const m = flipH && flipV ? [-1, 0, 0, -1] : flipH ? [-cos2, -sin2, -sin2, cos2] : [cos2, sin2, sin2, -cos2];
  const [cx, cy] = [rect.x + rect.width / 2, rect.y + rect.height / 2];
  // `+ 0` turns a -0 product into 0, as the Rust twin does.
  const mirror = (mask: SceneStyleMask): SceneStyleMask => {
    const [a, b, c, d, e, f] = mask.transform;
    const [x, y] = [e - cx, f - cy];
    return {
      ...mask,
      transform: [
        m[0] * a + m[1] * b + 0,
        m[2] * a + m[3] * b + 0,
        m[0] * c + m[1] * d + 0,
        m[2] * c + m[3] * d + 0,
        m[0] * x + m[1] * y + cx + 0,
        m[2] * x + m[3] * y + cy + 0,
      ],
    };
  };

  return {
    ...fields,
    layerStyleMasks: {
      ...(masks.source && { source: masks.source.map(mirror) }),
      ...(masks.post && { post: masks.post.map(mirror) }),
    },
  };
}

function rustRect(r: { x: number; y: number; width: number; height: number; rotation?: number }): RustRect {
  return { x: r.x, y: r.y, width: r.width, height: r.height, rotation: r.rotation ?? 0 };
}

function blendOf(box: RenderBox): string {
  return box.blendMode || 'normal';
}

// Decode a base64 thumbHash to a data-url so an unloaded slot shows the blur, not a flat grey frame.
function thumbHashDataUrl(thumbHashBase64: string | undefined): string | undefined {
  if (!thumbHashBase64) return undefined;
  try {
    const bytes = Uint8Array.from(atob(thumbHashBase64), c => c.charCodeAt(0));
    return thumbHashToDataURL(bytes);
  } catch {
    return undefined;
  }
}

// Версию можно растянуть на +30% её собственного размера — дальше кадр начинает мылить.
const RENDITION_UPSCALE_TOLERANCE = 1.3;

// Ступени лестницы от лёгкой к тяжёлой; имя поля называет длинную сторону версии. Ступень закрывает
// рендер до `px * RENDITION_UPSCALE_TOLERANCE`: 640 -> 832, 1280 -> 1664, 1920 -> всё, что выше.
// big_1920 — верх и жёсткий потолок лестницы: оригинал ступенью НЕ считается и на зуме не запрашивается никогда.
// THUMB (320) ступенью НЕ считается: он остаётся прогрессивным превью и целью рендера не бывает — рамка
// растёт от смены шаблона, и растянутый в неё thumb читается как каша, пока не доедет апгрейд.
const RENDITION_RUNGS: readonly { readonly field: 'small' | 'medium_1280' | 'big_1920'; readonly px: number }[] = [
  { field: 'small', px: 640 },
  { field: 'medium_1280', px: 1280 },
  { field: 'big_1920', px: 1920 },
];

// Лестница считает РЕНДЕРНЫЕ пиксели (scale уже включает dpr). Пропущенная ступень не ломает выбор:
// берётся ближайшая доступная сверху, а если сверху ничего нет — самая крупная снизу.
//
// Альбомы, сохранённые до разделения полей, держат в `small` файл 1280 — отличить его по адресу нельзя,
// поэтому `small` всегда судится как 640. Для таких альбомов это лишь означает переход на big_1920 после
// 832 рендерных пикселей: мыла не будет никогда, в худшем случае лишний трафик.
// Сессионный пол лестницы: ступень, которую фото уже ПОКАЗАЛО в этой вкладке, ниже не отдаётся — отдаление и
// возврат зума переиспользуют показанные пиксели (кэш байтов/текстур) вместо повторной прогулки по лестнице.
const sessionRungFloor = new Map<string, number>();

export function noteShownRendition(imageId: string | undefined, rungPx: number | undefined): void {
  if (!imageId || !rungPx) return;
  if ((sessionRungFloor.get(imageId) ?? 0) < rungPx) sessionRungFloor.set(imageId, rungPx);
}

/** Только для тестов: пол — модульный синглтон на всю вкладку и переживает контроллеры. */
export function resetSessionRungFloor(): void {
  sessionRungFloor.clear();
}

function selectAlbumImageSize(box: RenderImageBox, scale: number, originalFallback = false): string | undefined {
  const album = box.imageAlbum;
  if (!album) return undefined;
  const floorPx = sessionRungFloor.get(albumImageId(box) ?? '') ?? 0;
  const maxRendered = Math.max(Math.max(box.rect.width, box.rect.height) * scale, floorPx);
  // Ступень крупнее самого фото ничего не добавляет: сервер не увеличивает, и её файл — те же пиксели
  // оригинала. Нативный размер неизвестен (0 или нет поля) — потолка нет.
  const nativeMax = Math.max(album.width ?? 0, album.height ?? 0) || Infinity;

  let largestAvailable: string | undefined;
  let largestAvailablePx = 0;
  for (const rung of RENDITION_RUNGS) {
    // Взятая ступень уже покрыла оригинал целиком — следующие несут те же пиксели, но тяжелее.
    // Считаем только ВЗЯТЫЕ ступени: пропущенная (файла нет) потолок не поднимает.
    if (largestAvailablePx >= nativeMax) break;
    const url = album[rung.field];
    if (!url) continue;
    largestAvailable = url;
    largestAvailablePx = rung.px;
    // Ступень отдаётся, пока растяжение в пределах допуска. Иначе идём выше — но не выше лестницы.
    if (maxRendered <= rung.px * RENDITION_UPSCALE_TOLERANCE) return url;
  }

  // Ни одна ступень не закрывает кадр без растяжения — отдаём самую крупную ВЗЯТУЮ (обычно big_1920) прямо
  // растянутой. Оригинал камеры — многие мегабайты: на глубоком зуме он приезжает секундами, и лишняя
  // резкость этих секунд не стоит. Лестница ограничена сверху big_1920 на любой глубине зума.
  //
  // Ступеней нет вовсе: локальный blob свежей загрузки (байты уже в памяти), иначе thumb, иначе — ничего:
  // кадр держит заглушку, пока нарезка не доедет. Оригинал камеры по сети экран не тянет никогда.
  if (largestAvailable) return largestAvailable;
  // The client bake masters the stored 1280/1920 preview: a tierless photo (failed cut, legacy row) bakes from
  // its original, never from a stretched thumb or a blur.
  const drawable = browserDrawableOriginal(album.image);
  if (originalFallback && drawable) return drawable;
  return album.image?.startsWith('blob:') ? album.image : album.thumb;
}

// Mirrors ImageRenderer source precedence: real photo → template preview → baked pixel preview → blur.
function imageBoxUrl(box: RenderImageBox, scale = 1, originalFallback = false): string | undefined {
  if (box.layerStyleRendering === 'live') {
    // A HEIC original paints from its largest JPEG version on screen; print still reads the original.
    const album = box.imageAlbum;
    const original = box.imageUrl || browserDrawableOriginal(album?.image) || album?.big_1920 || album?.medium_1280;
    if (!original && box.imageAlbum) throw new Error(`Missing original live-style image asset: ${box.id}`);
    return original || undefined;
  }

  const own = selectAlbumImageSize(box, scale, originalFallback);
  // A placed photo still waiting for its tiers keeps its own blur, never the template sample of the slot.
  if (!own && box.imageAlbum?.image) return thumbHashDataUrl(box.imageAlbum.thumb_hash) || undefined;
  return (
    own ||
    box.previewSourceUrl ||
    box.previewPixelData ||
    thumbHashDataUrl(box.imageAlbum?.thumb_hash) ||
    thumbHashDataUrl(box.previewThumbHash) ||
    undefined
  );
}

// Empty photo frame placeholder (ported from ImageRenderer/placeholder-helper): bg + faint border.
const EMPTY_FRAME_FILL = '#c8d4de';
const EMPTY_FRAME_BORDER = '#e6e6e6';
const EMPTY_FRAME_ICON = '#9aa1b3';
/** Icon grid (24 units) side as a share of the frame's shorter side. */
const EMPTY_FRAME_ICON_SHARE = 0.12;

// Photo glyph centred on an empty frame (lucide «image» on its 24-unit grid), twin of placeholder_icon in
// engine/psd-render/core/src/adapter/mod.rs. Locked: clicks fall through to the placeholder, which owns the slot.
function placeholderIcon(id: string, r: RustRect): SceneVectorBox {
  const u = (Math.min(r.width, r.height) * EMPTY_FRAME_ICON_SHARE) / 24;
  const ox = r.x + r.width / 2 - 12 * u;
  const oy = r.y + r.height / 2 - 12 * u;
  return {
    type: 'vector',
    id: `${id}__empty-icon`,
    rect: { x: ox, y: oy, width: 24 * u, height: 24 * u, rotation: r.rotation },
    path: [
      ...roundedRectSegs(ox + 3 * u, oy + 3 * u, 18 * u, 18 * u, [2 * u, 2 * u, 2 * u, 2 * u]),
      ...ellipseSegs(ox + 7 * u, oy + 7 * u, 4 * u, 4 * u),
      { op: 'M', pts: [ox + 21 * u, oy + 15 * u] },
      { op: 'L', pts: [ox + 16.5 * u, oy + 10.5 * u] },
      { op: 'L', pts: [ox + 6 * u, oy + 21 * u] },
    ],
    stroke: { color: EMPTY_FRAME_ICON, width: 2 * u, dash: [], lineCap: 'round', lineJoin: 'round' },
    fillOpacity: 1,
    blend: 'normal',
    evenOdd: false,
    locked: true,
  };
}

/** Book-level frame stroke — the album default a frame falls back to when it carries no stroke effect of its own. */
export interface SceneFrameStroke {
  color: string;
  width: number;
}

// Своя обводка кадра ?? альбомный дефолт. Силуэтная рамка (маска или замкнутый контур) обводки не получает
// вообще: прямоугольная линия вокруг вырезанного кадра — не то, что видит пользователь, и правило одинаково
// для кадра с фото и пустого. Ширина отдаётся СЫРОЙ, в книжных px: масштаб накладывает scaleRustDrawable.
function resolveFrameStroke(
  box: RenderImageBox,
  sceneDefault: SceneFrameStroke | undefined,
  silhouetted: boolean,
  defaultAllowed: boolean,
): SceneFrameStroke | undefined {
  if (silhouetted) return undefined;
  const strokes = box.effects?.strokes;
  const own = strokes?.find(s => s.enabled !== false);
  // Непустой список без единой включённой записи — это ЯВНОЕ «без обводки», и альбомный дефолт им
  // отменяется. Списка нет вовсе (старые альбомы, рамка ничего не говорила) — дефолт как раньше.
  if (!own && !!strokes?.length) return undefined;
  // Своя обводка рисуется у любой рамки; запрет по геометрии (вылет, обложка) снимает только ДЕФОЛТ.
  const effective = own ? { color: own.color, width: own.width } : defaultAllowed ? sceneDefault : undefined;
  if (!effective?.color || !(effective.width > 0)) return undefined;
  return effective;
}

/** A live layer owns its outline only through a stroke that draws something. Twin of Rust `has_live_strokes`. */
function hasLiveStrokes(box: RenderBox): boolean {
  return (
    box.layerStyleRendering === 'live' && !!box.effects?.strokes?.some(s => s.enabled !== false && s.opacity !== 0 && s.width > 0)
  );
}

/** First closed PSD contour, absolute book px: the photo cut and the silhouette its effects follow. Twin of Rust `image_clip`. */
function closedPathClipOf(box: RenderImageBox, offsetX: number, offsetY: number): SceneClipKnot[] | undefined {
  const closedPath = box.paths?.find(p => !p.open && p.knots && p.knots.length >= 2);
  return closedPath ? bezierPathToClipKnots(closedPath, box.rect.x + offsetX, box.rect.y + offsetY) : undefined;
}

// A frame marked up in the Designer holds its mask long before a photo lands in it, so the placeholder
// carries maskRef too — otherwise a mask dropped on an empty frame would show nothing at all.
function placeholderShape(
  box: RenderImageBox,
  rect: RustRect,
  maskRef: string | undefined,
  stroke: SceneFrameStroke | undefined,
  decor: SceneFrameDecor | undefined,
  maskRect: RustMaskRect | undefined,
): ZoneShape {
  // rect остаётся ПОЛНЫМ всегда: это геометрия бокса — по ней идут хит-тест, привязки, превью дропа и
  // раскладка альфа-маски. Линию внутрь границы уводит растеризатор по флагу strokeInside, ровно как он
  // уже делает это для кадра с фото (stroke_frame).
  return {
    type: 'shape',
    id: box.id,
    rect,
    fill: box.color || EMPTY_FRAME_FILL,
    stroke: stroke ? stroke.color : EMPTY_FRAME_BORDER,
    // A frame whose decoration already draws its own outline needs no service border under it.
    strokeWidth: stroke ? stroke.width : decor?.strokes?.length || hasLiveStrokes(box) ? 0 : 1,
    ...(stroke ? { strokeInside: true } : {}),
    dash: [],
    blend: 'normal',
    interactive: true, // an empty image frame is selectable content, unlike zone/selection shapes
    ...(maskRef ? { maskRef } : {}),
    ...(maskRect ? { maskRect } : {}),
    ...(decor ? { decor } : {}),
  };
}

/** One layer a frame can be clipped to: where it lies and the box itself (its pixels are the mask). */
interface MaskLayer {
  rect: RustRect;
  box: RenderBox;
}

/**
 * Where a frame's mask lies: the rect of the layer it is clipped to (Photoshop's clipping mask), so the mask
 * stands still and the frame slides under it. `undefined` when the mask belongs to the frame itself (picked
 * from the «Маски» panel) or its layer is no longer on the spread.
 */
function maskRectOf(
  box: RenderImageBox,
  layers: ReadonlyMap<string, MaskLayer>,
  offsetX = 0,
  offsetY = 0,
): RustMaskRect | undefined {
  // An explicit frame describes the accompanying compatibility asset; base-id inference is legacy fallback.
  // Twin of the Rust adapter's `own_mask_frame`: the frame over the box's own mask is marked when the model calls
  // it the box's own; the model's `follows` stays out of the scene.
  if (box.maskRect && box.maskDataUrl) {
    const { follows: _follows, mirrors: _mirrors, ...frame } = scaleMaskRect(box.maskRect, 1, 1, offsetX, offsetY);
    return { rotation: 0, ...frame, ...(maskMirrorsWithBox(box.maskRect) ? { mirrors: true } : {}) };
  }

  return box.maskBaseBoxId ? layers.get(box.maskBaseBoxId)?.rect : undefined;
}

/** Absolute rects of every box on the spread, by id — a clipped frame reads its mask layer's rect from here. */
function collectMaskLayers(boxes: readonly RenderBox[], offsetX: number, offsetY: number, into: Map<string, MaskLayer>): void {
  for (const box of boxes) {
    const rect = rustRect(box.rect);
    rect.x += offsetX;
    rect.y += offsetY;
    into.set(box.id, { rect, box });
    const children = (box as { children?: readonly RenderBox[] }).children;
    if (children?.length) collectMaskLayers(children, offsetX + box.rect.x, offsetY + box.rect.y, into);
  }
}

// Длинные стороны экранных версий; те же потолки, что у ступеней `medium_1280` и `big_1920` фотолестницы.
const MEDIUM_RENDITION_PX = 1280;
const LARGE_RENDITION_PX = 1920;

// ObjectRenderer takes pixelData first (immediate, baked effects); fall back to remote, then blur.
// `medium` (1280) и `large` (1920) — экранные версии, и целью каждая бывает, только пока бокс в её пиксели
// укладывается (scale уже включает dpr): крупный декоративный слой на 100 % зуме иначе рисуется растянутой
// нарезкой вместо своего нативного оригинала. Выше 1920 идёт оригинал — у снимка фона это размер разворота
// (до 6000 px), и тянуть его на каждый показ незачем. Ниже `url` обе остаются запасными — у ассета без
// оригинала рисовать больше нечем; у снимков, снятых до появления тира 1920, `large` просто нет.
// `thumb` sits behind `url`: as a final target it would pin 320 px on every asset with no medium rendition.
function objectBoxUrl(box: RenderObjectBox, scale = 1): string | undefined {
  if (box.layerStyleRendering === 'live') {
    const original = box.pixelData || box.url;
    if (!original) throw new Error(`Missing original live-style object asset: ${box.id}`);
    return original;
  }

  const medium = box.versions?.medium;
  const large = box.versions?.large;
  const maxRendered = Math.max(box.rect.width, box.rect.height) * scale;
  const covers = (px: number): boolean => maxRendered <= px * RENDITION_UPSCALE_TOLERANCE;
  return (
    box.pixelData ||
    (covers(MEDIUM_RENDITION_PX) ? medium : undefined) ||
    (covers(LARGE_RENDITION_PX) ? large : undefined) ||
    box.url ||
    large ||
    medium ||
    box.versions?.thumb ||
    thumbHashDataUrl(box.versions?.thumbHash) ||
    undefined
  );
}

interface SmartObjectPlacement {
  url?: string;
  /** Set only on the `source` branch — the affine belongs to the source image, not to the box content. */
  transform?: readonly [number, number, number, number, number, number];
  kind: 'pixelData' | 'source' | 'box';
}

// The affine maps SOURCE pixels to canvas — `[a,b]` ride the source's x, `[c,d]` its y — so a rendition of the
// source fits it only after each column is divided by the scale of ITS OWN axis; the resize caps the long side and
// rounds the short one, so the two scales differ on anything but a square. The translation is already in canvas
// space. Without a usable scale the rendition is skipped rather than drawn through the natural-size matrix, which
// would shrink it into the corner.
function sourceTierPlacement(box: RenderSmartObjectBox): SmartObjectPlacement | undefined {
  const tier = box.sourceVersions?.medium;
  if (!tier?.path || !box.sourceUrl) return undefined;
  if (!box.sourceTransform) return { url: tier.path, kind: 'source' };

  const kx = tier.width > 0 && box.sourceNaturalWidth ? tier.width / box.sourceNaturalWidth : 0;
  const ky = tier.height > 0 && box.sourceNaturalHeight ? tier.height / box.sourceNaturalHeight : 0;
  if (!(kx > 0) || !(ky > 0)) return undefined;

  const [a, b, c, d, e, f] = box.sourceTransform;
  return { url: tier.path, transform: [a / kx, b / kx, c / ky, d / ky, e, f], kind: 'source' };
}

// One decision for both scene building and the progressive ladder: the affine rides whatever url wins here, so a
// second, independently written precedence would silently place the source through the wrong matrix.
function resolveSmartObjectPlacement(box: RenderSmartObjectBox): SmartObjectPlacement {
  if (box.layerStyleRendering === 'live') {
    const original = box.pixelData || box.url;
    if (!original) throw new Error(`Missing original live-style smart-object asset: ${box.id}`);
    return { url: original, kind: 'box' };
  }

  if (box.pixelData) return { url: box.pixelData, kind: 'pixelData' };

  const tier = sourceTierPlacement(box);
  if (tier) return tier;

  if (box.sourceUrl) return { url: box.sourceUrl, transform: box.sourceTransform, kind: 'source' };

  return {
    url: box.versions?.medium || box.url || box.versions?.thumb || thumbHashDataUrl(box.thumbHash) || undefined,
    kind: 'box',
  };
}

// The editor builds its scene through build_scene — the print adapter — so browser and printer agree by construction.
// That adapter carries no mirror of `sourceVersions`: handed a source rendition it would still place it through the
// affine of the full-size source, drawing the smart object shrunk into the corner of its box. Both sides stay on the
// full source; the editor trades the rendition's memory win for that parity.
export function withoutSourceTiers(boxes: readonly RenderBox[]): readonly RenderBox[] {
  let changed = false;
  const stripped = boxes.map((box: RenderBox): RenderBox => {
    if (box.type === 'group') {
      const group = box as RenderGroupBox;
      const children = withoutSourceTiers(group.children);
      if (children === group.children) return box;
      changed = true;
      return { ...group, children: children as RenderBox[] };
    }

    if (box.type !== 'smartObject' || !(box as RenderSmartObjectBox).sourceVersions) return box;
    changed = true;
    const { sourceVersions: _tiers, ...rest } = box as RenderSmartObjectBox;
    return rest;
  });

  return changed ? stripped : boxes;
}

// Хосту без подписанного исходника его адрес — 403: кадр без нарезок рисуется с превью, превью-исходник отброшен.
// Менять нечего — тот же массив, как у `withoutSourceTiers`: холст зовёт это на каждый рендер.
export function withoutOriginalImages(boxes: RenderBox[]): RenderBox[] {
  let changed = false;
  const stripped = boxes.map((box): RenderBox => {
    if (box.type === 'group') {
      const group = box as RenderGroupBox;
      const children = withoutOriginalImages(group.children);
      if (children === group.children) return box;
      changed = true;
      return { ...group, children };
    }

    const album = box.type === 'image' ? (box as RenderImageBox).imageAlbum : undefined;
    if (!album?.image) return box;
    changed = true;
    const { image, thumb, ...rest } = album;
    const imageAlbum = thumb === image ? rest : { ...rest, thumb };
    const ownUrl = (thumb !== image && !!thumb) || RENDITION_RUNGS.some(rung => !!rest[rung.field]);
    if (ownUrl) return { ...box, imageAlbum } as RenderImageBox;
    // У фото не осталось ни одного своего адреса: образец шаблона встал бы в рамку раньше размытого превью
    // самого фото, и клиент увидел бы в ней чужой снимок. Лучше размытое превью или пустая рамка.
    const { previewSourceUrl: _sample, previewPixelData: _pixels, ...frame } = box as RenderImageBox;
    return { ...frame, imageAlbum };
  });

  return changed ? stripped : boxes;
}

function vectorUrl(box: RenderVectorBox): string | undefined {
  return box.vector.pixelData || box.vector.url || undefined;
}

// Every box carrying the same mask payload shares one decode. The ref is derived from the URL, never
// from a carrier box id: a carrier-named ref migrates when another box adopts the same mask, turning an
// already-decoded mask into a cache miss — and requiresRef then blanks every carrier until the re-decode.
function createMaskRefRegistry(addRef: (ref: string, url: string, mode?: typeof BLOCKING_MASK) => void) {
  const registered = new Set<string>();
  return (box: RenderBox, mode?: typeof BLOCKING_MASK): string | undefined => {
    const mask = (box as { maskDataUrl?: string }).maskDataUrl;
    if (!mask) return undefined;
    const ref = `mask:${mask}`;
    if (!registered.has(ref) || mode) {
      registered.add(ref);
      addRef(ref, mask, mode);
    }

    return ref;
  };
}

// A mask the engine needs before it can draw as print does (an adjustment mask, else the scene is refused; a group
// mask, else the group paints unmasked) decodes in the first wave, never after a paint that goes without it.
const BLOCKING_MASK = { blocking: true } as const;

// `undefined` when the tie resolves to no layer that draws: then the frame falls back to its own mask asset.
function clipMaskBaseOf(
  box: RenderBox,
  lookup: (id: string) => RenderBox | undefined,
  scale: number,
): { base: RenderObjectBox; url: string } | undefined {
  if (box.maskRect && (box as RenderImageBox).maskDataUrl) return undefined;
  const baseId = (box as RenderImageBox).maskBaseBoxId;
  const base = baseId ? lookup(baseId) : undefined;
  if (!base || (base.type !== 'object' && base.type !== 'background')) return undefined;
  const url = objectBoxUrl(base as RenderObjectBox, scale);
  return url ? { base: base as RenderObjectBox, url } : undefined;
}

// One answer for the scene's `maskRef` and the decoder's `requiresRef`; own mask registers first — the shared ref order.
function createCuttingMaskRef(
  maskRefOf: (box: RenderBox) => string | undefined,
  lookup: (id: string) => RenderBox | undefined,
  scale: number,
  addBaseRef: (base: RenderObjectBox, url: string) => void,
) {
  return (box: RenderBox): string | undefined => {
    const own = maskRefOf(box);
    if (own && box.maskRect) return own;
    const clip = clipMaskBaseOf(box, lookup, scale);
    if (!clip) return own;
    addBaseRef(clip.base, clip.url);
    return clip.base.id;
  };
}

// The frame PNG is stretched so its normalized window rect maps exactly onto the box rect.
function frameOverlayRect(rect: RustRect, win: { x: number; y: number; width: number; height: number }): RustRect {
  const width = rect.width / win.width;
  const height = rect.height / win.height;
  return {
    x: rect.x - win.x * width,
    y: rect.y - win.y * height,
    width,
    height,
    rotation: rect.rotation,
  };
}

function frameOf(box: RenderBox): { url: string; win: { x: number; y: number; width: number; height: number } } | undefined {
  const b = box as RenderImageBox;
  if (box.type !== 'image' || !b.frameUrl || !b.frameWindow) return undefined;
  if (!(b.frameWindow.width > 0) || !(b.frameWindow.height > 0)) return undefined;
  return { url: b.frameUrl, win: b.frameWindow };
}

// A hand-bonded clipper has no silhouette until its first bake: drawn unclipped, it would show its texture whole.
const isUnbakedCompoundClip = (box: RenderBox): boolean =>
  !!(box as RenderObjectBox).compoundClipBakeRecipe && !(box as RenderObjectBox).maskDataUrl;

function liveTextClipBases(boxes: readonly RenderBox[]): Map<string, string[]> {
  const counts = new Map<string, number>();
  const count = (siblings: readonly RenderBox[]): void => {
    for (const box of siblings) {
      counts.set(box.id, (counts.get(box.id) ?? 0) + 1);
      if (box.type === 'group') count(box.children);
    }
  };

  count(boxes);
  const result = new Map<string, string[]>();
  const visit = (siblings: readonly RenderBox[]): void => {
    let base: RenderTextBox | undefined;
    for (const box of siblings) {
      if (box.visible === false) {
        base = undefined;
        continue;
      }

      if (box.type === 'group') {
        visit(box.children);
        base = undefined;
        continue;
      }

      if (box.type === 'text') {
        const masked = box as RenderTextBox & { maskDataUrl?: string; maskBaseBoxId?: string };
        base = counts.get(box.id) === 1 && !masked.maskDataUrl && !masked.maskBaseBoxId ? box : undefined;
        continue;
      }

      const recipe = box.type === 'object' ? box.compoundClipBakeRecipe : undefined;
      if (
        base &&
        recipe &&
        !recipe.requiresRgbComposite &&
        !recipe.preserveStaticMask &&
        recipe.leafBoxIds.length === 1 &&
        recipe.leafBoxIds[0] === base.id &&
        counts.get(box.id) === 1 &&
        (box.rect.rotation ?? 0) === (base.rect.rotation ?? 0)
      ) {
        result.set(box.id, [base.id]);
      } else base = undefined;
    }
  };

  visit(boxes);
  return result;
}

// toSpreadScene (kept for psd-render-lab) has no group node: a PSD group composite (ADR 0043) loses its styles,
// opacity and isolation here. build_scene, which the editor uses, draws it (`Drawable::Group`).
let flattenedPsdGroupWarned = false;
function warnFlattenedPsdGroup(): void {
  if (flattenedPsdGroupWarned) return;
  flattenedPsdGroupWarned = true;
  console.warn(
    '[toSpreadScene] psdGroup composite flattened: group styles, opacity and isolation not drawn; build_scene draws it',
  );
}

/** Заливка страницы — синтетический вектор `page-fill-<n>` обоих коллекторов (collect-render-boxes.ts, scene_build.rs). */
const isPageFillId = (id: string): boolean => id.startsWith('page-fill-');

export function toSpreadScene(
  boxes: readonly RenderBox[],
  viewport: { width: number; height: number },
  textDeps?: AdapterDeps,
  scale = 1,
  zones?: ZonePrintSizes,
  zoneVisibility?: ZoneVisibility,
  zoneStyle?: ZoneStyle,
  liveCropOverrides?: ReadonlyMap<string, LiveCropRect>,
  liveTrimOverrides?: ReadonlyMap<string, LiveTrimOverride>,
  frameStroke?: SceneFrameStroke,
  albumStrokeScope?: AlbumStrokeScope,
  // Ступень версии выбирается ОТДЕЛЬНО от геометрии: буфер сцены капается по памяти, а фото на экране от этого
  // мельче не становится. По умолчанию — тот же scale, что и геометрия.
  renditionScale = scale,
  // Где искать слой-маску. Живой жест рисует ПОДНЯТУЮ рамку отдельной мини-сценой, и слоя, которым она
  // обрезана, в этой сцене нет: без полного списка маска растянулась бы на весь прямоугольник рамки, и на
  // время перетаскивания рамка принимала бы форму облака целиком. Рисуются по-прежнему только `boxes`.
  maskLayerBoxes: readonly RenderBox[] = boxes,
  // Preview bake only: a photo with no tier draws its original. The live canvas never pulls it over the network.
  originalFallback = false,
): SpreadScene {
  const drawables: RustDrawable[] = [];
  const liveClips = textDeps ? liveTextClipBases(boxes) : new Map<string, string[]>();
  const imageRefs: SceneImageRef[] = [];
  const maskLayers = new Map<string, MaskLayer>();
  collectMaskLayers(maskLayerBoxes, 0, 0, maskLayers);
  const seen = new Set<string>();
  const backgroundIds = new Set<string>();

  const addRef = (ref: string, url: string, mode?: Pick<SceneImageRef, 'preservePngRgba' | 'blocking'>): void => {
    if (seen.has(ref)) {
      const existing = imageRefs.find(r => r.ref === ref);
      if (existing && mode?.preservePngRgba) existing.preservePngRgba = true;
      if (existing && mode?.blocking) existing.blocking = true;
      return;
    }

    seen.add(ref);
    imageRefs.push({ ref, url, ...mode });
  };

  // Decode the PSD clipping-mask alpha as its own image; without it a masked decor texture paints full-bleed opaque.
  const maskRefOf = createMaskRefRegistry(addRef);

  // A clipped frame is cut by the mask LAYER's own pixels, as in Photoshop: a repainted layer re-cuts it by itself.
  const maskLayerOf = (id: string): RenderBox | undefined => maskLayers.get(id)?.box;
  const cuttingMaskRefOf = createCuttingMaskRef(maskRefOf, maskLayerOf, renditionScale, (base, url) =>
    addRef(base.id, url, base.layerStyleRendering === 'live' ? { preservePngRgba: true } : undefined),
  );
  // Силуэт рамке даёт только та маска, что реально режет: слой-маска без пикселей оставляет её прямоугольником.
  const cutByMask = (box: RenderBox): boolean =>
    !!(box as { maskDataUrl?: string }).maskDataUrl || !!clipMaskBaseOf(box, maskLayerOf, renditionScale);

  // Области нет — судить вылет нечем, ведём себя как раньше (запрета нет). Область приходит отдельным
  // аргументом, а НЕ из `zones`: `zones` заодно включает отрисовку технических зон, и живой редактор их
  // сюда не отдаёт. Геометрия бокса приводится к АБСОЛЮТНОЙ: смещения страницы и групп уже накоплены в
  // offsetX/offsetY.
  //
  // Запечённая шаблоном обводка вылету не родня и геометрией не читается — рамка приносит её флагом
  // (`frameStrokeBaked`), и альбомный дефолт ей не положен ни в каком месте страницы: линия легла бы
  // второй. Тот же запрет в Rust (`frame_stroke_of`).
  const albumStrokeAllowed = (box: RenderImageBox, offsetX: number, offsetY: number): boolean =>
    !box.frameStrokeBaked &&
    (!albumStrokeScope ||
      frameStrokeAllowedIn(
        { x: box.rect.x + offsetX, y: box.rect.y + offsetY, width: box.rect.width, height: box.rect.height },
        albumStrokeScope,
      ));

  const pushObjectDrawable = (
    box: RenderBox,
    url: string,
    offsetX: number,
    offsetY: number,
    sourceTransform?: readonly [number, number, number, number, number, number],
    contentRect?: RenderRect,
    rectOverride?: RenderRect,
  ): void => {
    const ref = box.id;
    addRef(ref, url, box.layerStyleRendering === 'live' ? { preservePngRgba: true } : undefined);
    const rect = rustRect(rectOverride ?? box.rect);
    rect.x += offsetX;
    rect.y += offsetY;
    // contentRect shifts like rect itself (fixed placement) — unlike imageRect it is NOT rect-relative.
    let wireContentRect: RustRect | undefined;
    if (contentRect) {
      wireContentRect = rustRect(contentRect);
      wireContentRect.x += offsetX;
      wireContentRect.y += offsetY;
    }

    // Слой, чьи эффекты НЕ впечатаны в пиксели, несёт их живыми: движок рисует обводку, тень и свечение по
    // его собственной форме. Такие эффекты принадлежат слою и едут вместе с ним — отдельной плитки рядом нет.
    // У слоя нет легаси-пары `stroke`/`strokeWidth`: одну простую обводку, кроме декора, нарисовать некому.
    const decor = withoutLiveDecor(
      box,
      frameDecorOf(box as RenderImageBox, closedPathClipOf(box as RenderImageBox, offsetX, offsetY), cutByMask(box), false),
    );
    // Слот клип-группы, пока он ещё декор, режется тем же слоем-маской, что и рамка, — и уезжает под неё так же.
    const maskRef = cuttingMaskRefOf(box);
    const maskRect = maskRectOf(box as RenderImageBox, maskLayers, offsetX, offsetY);
    const flipH = box.flipHorizontal === true;
    const flipV = box.flipVertical === true;
    const live = mirroredStyleMasks(liveStyleFields(box, offsetX, offsetY), flipH, flipV, rect);
    drawables.push({
      type: 'object',
      ...live,
      id: box.id,
      rect,
      imageRef: ref,
      maskRef,
      ...(liveClips.has(box.id) ? { clipBaseIds: liveClips.get(box.id) } : {}),
      ...(maskRect ? { maskRect } : {}),
      ...(decor ? { decor } : {}),
      ...(flipH ? { flipH: true } : {}),
      ...(flipV ? { flipV: true } : {}),
      blend: blendOf(box),
      opacity: box.opacity,
      contentRect: wireContentRect,
      ...(sourceTransform && box.type === 'smartObject' && box.smartFilters?.length ? { smartFilters: box.smartFilters } : {}),
      sourceTransform: sourceTransform
        ? [
            sourceTransform[0],
            sourceTransform[1],
            sourceTransform[2],
            sourceTransform[3],
            sourceTransform[4] + offsetX,
            sourceTransform[5] + offsetY,
          ]
        : undefined,
    });
  };

  const walk = (box: RenderBox, offsetX: number, offsetY: number): void => {
    // Group children carry their own visibility — the top-level loop can't see them.
    if (box.visible === false || ((box.type === 'text' || box.layerStyleRendering === 'live') && box.opacity === 0)) return;
    styleMaskRefs(box, (ref, url) => addRef(ref, url, STYLE_RESOURCE));
    const pattern =
      (box.type === 'text' || box.layerStyleRendering === 'live') &&
      box.effects?.patternOverlay?.enabled !== false &&
      box.effects?.patternOverlay?.opacity !== 0
        ? box.effects?.patternOverlay?.pattern
        : undefined;
    const patternUrl = pattern?.url || pattern?.pixelData;
    if (patternUrl)
      addRef(`${box.id}__pattern`, patternUrl, box.layerStyleRendering === 'live' ? STYLE_RESOURCE : { blocking: true });
    switch (box.type) {
      case 'background':
      case 'object': {
        const url = objectBoxUrl(box, renditionScale);
        if (!url) return;
        if (isUnbakedCompoundClip(box) && !liveClips.has(box.id)) return;
        if (box.type === 'background') backgroundIds.add(box.id);
        const trim = liveTrimOverrides?.get(box.id);
        pushObjectDrawable(box, url, offsetX, offsetY, undefined, trim?.content ?? box.contentRect, trim?.window);
        break;
      }

      case 'smartObject': {
        const placement = resolveSmartObjectPlacement(box);
        if (!placement.url) return;
        const trim = liveTrimOverrides?.get(box.id);
        pushObjectDrawable(
          box,
          placement.url,
          offsetX,
          offsetY,
          placement.transform,
          trim?.content ?? box.contentRect,
          trim?.window,
        );
        break;
      }

      case 'vector': {
        // Konva renders vectors from paths+fill+stroke (ignoring pixelData), so mirror that first.
        const vec = buildVectorDrawable(box as RenderVectorBox, offsetX, offsetY);
        if (vec) {
          // Live styles: PS Fill Opacity folds into the vector's own (one `fillOpacity` key), as in the Rust adapter.
          const rect = rustRect(box.rect);
          rect.x += offsetX;
          rect.y += offsetY;
          const live = mirroredStyleMasks(
            liveStyleFields(box, offsetX, offsetY),
            box.flipHorizontal === true,
            box.flipVertical === true,
            rect,
          );
          drawables.push(
            box.layerStyleRendering === 'live' ? { ...vec, ...live, fillOpacity: vec.fillOpacity * (box.fillOpacity ?? 1) } : vec,
          );
          break;
        }

        const url = vectorUrl(box);
        if (url) pushObjectDrawable(box, url, offsetX, offsetY);
        break;
      }

      case 'adjustment': {
        const adj = (box as RenderAdjustmentBox).adjustment;
        const { maskDataUrl, maskUrl, maskFileId: _maskFileId, ...params } = adj;
        let rect: RustRect | undefined;
        if (adj.clipped && !adj.clipBaseBoxId) {
          rect = rustRect(box.rect);
          rect.x += offsetX;
          rect.y += offsetY;
        }

        drawables.push({
          ...params,
          type: 'adjustment',
          id: box.id,
          rect,
          opacity: box.opacity,
          blend: box.blendMode ?? 'normal',
          maskRef:
            !adjustmentIsActive(box as RenderAdjustmentBox) || adj.maskDisabled
              ? undefined
              : maskRefOf(Object.assign({}, box, { maskDataUrl: maskDataUrl || maskUrl }), BLOCKING_MASK),
          maskVector: adj.maskVector
            ? { ...adj.maskVector, paths: scalePaths(adj.maskVector.paths, 1, 1, offsetX, offsetY) }
            : undefined,
          maskRect: adj.maskRect ? { ...adj.maskRect, x: adj.maskRect.x + offsetX, y: adj.maskRect.y + offsetY } : undefined,
          lutRef: adj.kind === 'colorLookup' && adj.lutResourceId ? `adjustment:${adj.lutResourceId}` : undefined,
          profileRef: adj.kind === 'colorLookup' && adj.profileResourceId ? `adjustment:${adj.profileResourceId}` : undefined,
          documentProfileRef: adj.documentProfileResourceId ? `adjustment:${adj.documentProfileResourceId}` : undefined,
        });
        break;
      }

      case 'image': {
        // Drawn above the photo with the box's own id, so hit-testing the border still selects the slot.
        const pushFrameOverlay = (boxRect: RustRect): void => {
          const frame = frameOf(box);
          if (!frame) return;
          const frameRef = `${box.id}__frame`;
          addRef(frameRef, frame.url);
          drawables.push({
            type: 'object',
            id: box.id,
            rect: frameOverlayRect(boxRect, frame.win),
            imageRef: frameRef,
            blend: 'normal',
            opacity: box.opacity,
          });
        };

        // Контур считается до развилки «фото / пустая рамка»: обводку он отменяет одинаково в обеих ветках.
        // Один и тот же контур едет и в обрезку фото, и в эффекты рамки: линия и тень идут по силуэту,
        // а пустая рамка обязана выглядеть так же, как заполненная, — значит контур нужен и ей.
        const clip = closedPathClipOf(box, offsetX, offsetY);
        const masked = cutByMask(box);
        const decor = withoutLiveDecor(box, frameDecorOf(box, clip, masked));
        const albumStroke = resolveFrameStroke(box, frameStroke, masked || !!clip, albumStrokeAllowed(box, offsetX, offsetY));
        // Обводки рисует кто-то один: есть свои — легаси-пара несёт только альбомный дефолт.
        const stroke = decor?.strokes?.length || hasLiveStrokes(box) ? undefined : albumStroke;

        const maskRect = maskRectOf(box, maskLayers, offsetX, offsetY);
        const url = imageBoxUrl(box, renditionScale, originalFallback);
        if (!url) {
          const phRect = rustRect(box.rect);
          phRect.x += offsetX;
          phRect.y += offsetY;
          drawables.push({
            ...placeholderShape(box, phRect, cuttingMaskRefOf(box), stroke, decor, maskRect),
            ...liveStyleFields(box, offsetX, offsetY),
            ...(box.layerStyleRendering === 'live' ? { opacity: box.opacity, blend: blendOf(box) } : {}),
          });
          drawables.push(placeholderIcon(box.id, phRect));
          pushFrameOverlay(phRect);
          return;
        }

        const ref = box.id;
        addRef(ref, url, box.layerStyleRendering === 'live' ? { preservePngRgba: true } : undefined);
        const frameMaskRef = cuttingMaskRefOf(box);
        const rect = rustRect(box.rect);
        rect.x += offsetX;
        rect.y += offsetY;
        // A live crop gesture substitutes the photo placement without a store round-trip (container-relative coords).
        const ir = liveCropOverrides?.get(box.id) ?? box.imageRect;
        const imageRect: RustRect | undefined = ir
          ? { x: rect.x + ir.x, y: rect.y + ir.y, width: ir.width, height: ir.height, rotation: ir.rotation ?? 0 }
          : undefined;
        drawables.push({
          type: 'image',
          ...liveStyleFields(box, offsetX, offsetY),
          id: box.id,
          rect,
          imageRef: ref,
          imageRect,
          maskRef: frameMaskRef,
          ...(maskRect ? { maskRect } : {}),
          clip,
          flipH: !!box.flipHorizontal,
          flipV: !!box.flipVertical,
          blend: blendOf(box),
          opacity: box.opacity,
          // rect остаётся ПОЛНЫМ: по нему живут хит-тест и посадка фото. Внутреннюю геометрию линии
          // (обводка целиком внутри рамки) считает растеризатор в Rust.
          ...(stroke ? { stroke: stroke.color, strokeWidth: stroke.width } : {}),
          ...(decor ? { decor } : {}),
        });
        pushFrameOverlay(rect);
        break;
      }

      case 'text': {
        if (!textDeps) return;
        const tb = toTextSceneBox(box as RenderTextBox, textDeps, scale);
        if (offsetX === 0 && offsetY === 0) {
          drawables.push(tb);
        } else {
          drawables.push({
            ...tb,
            ...transformEffectOrigins(tb, [1, 0, 0, 1, offsetX, offsetY]),
            rect: { ...tb.rect, x: tb.rect.x + offsetX, y: tb.rect.y + offsetY },
            inkRect: tb.inkRect ? { ...tb.inkRect, x: tb.inkRect.x + offsetX, y: tb.inkRect.y + offsetY } : undefined,
            glyphs: tb.glyphs.map(g => ({ ...g, x: g.x + offsetX, y: g.y + offsetY })),
            decorations: tb.decorations.map(d => ({ ...d, x: d.x + offsetX, y: d.y + offsetY })),
            transform: tb.transform ? offsetAffine(tb.transform as Affine, offsetX, offsetY) : undefined,
            fillGradient: tb.fillGradient ? shiftSceneVectorFill(tb.fillGradient, offsetX, offsetY) : undefined,
            strokes: tb.strokes.map(st => transformStrokeGradient(st, [1, 0, 0, 1, offsetX, offsetY])),
            gradientOverlay: tb.gradientOverlay
              ? transformGradientOverlay(tb.gradientOverlay, [1, 0, 0, 1, offsetX, offsetY])
              : undefined,
            ...(tb.extraGradientOverlays && {
              extraGradientOverlays: tb.extraGradientOverlays.map(g =>
                transformGradientOverlay(g, [1, 0, 0, 1, offsetX, offsetY]),
              ),
            }),
          });
        }

        break;
      }

      case 'group': {
        if ((box as RenderGroupBox).psdGroup) warnFlattenedPsdGroup();
        for (const child of (box as RenderGroupBox).children) {
          walk(child, offsetX + box.rect.x, offsetY + box.rect.y);
        }

        break;
      }

      default:
        return;
    }
  };

  for (const box of boxes) {
    if (box.visible === false) continue;
    walk(box, 0, 0);
  }

  // One box can expand into several drawables (tiers, placeholders), so the flag is stamped by id
  // after the walk rather than at every push site.
  const lockedIds = collectLockedRenderIds(boxes);
  for (const drawable of drawables) {
    if (drawable.type === 'object' || drawable.type === 'image') {
      const source = maskLayers.get(drawable.id)?.box as RenderImageBox | undefined;
      if (source?.maskBaseBoxId) drawable.clipBaseBoxId = source.maskBaseBoxId;
    }
  }

  if (lockedIds.size > 0) {
    for (const d of drawables) {
      if (lockedIds.has(d.id)) d.locked = true;
    }
  }

  // Закон z-порядка, зеркало Rust build_scene (adapter/mod.rs, твин golden_scene_parity): заливка страницы <
  // подложка < всё остальное. Порядок drawables — порядок боксов модели, и фон стоит первым лишь по соглашению:
  // пересборка раскладки может вернуть его после рамок, и он закрасил бы фотографии; опустить же его ПОД
  // заливку значило бы спрятать сам фон под «Цветом фона». Сортировка стабильная (ES2019) — взаимный порядок
  // внутри уровня не меняется.
  const zTier = (d: RustDrawable): number => (isPageFillId(d.id) ? 0 : backgroundIds.has(d.id) ? 1 : 2);
  drawables.sort((a, b) => zTier(a) - zTier(b));

  // Zones draw on top of content, in absolute print-px scene coords.
  if (zones) drawables.push(...buildZoneShapes(zones, zoneVisibility, zoneStyle));

  const scene = {
    viewport: spreadBufferSize(viewport, scale),
    drawables: scale === 1 ? drawables : drawables.map(d => scaleRustDrawable(d, scale)),
    // Альбомный дефолт едет в корень СЫРЫМ, в книжных px: масштаб уже вшит в ширину каждого drawable,
    // а домножь его ещё и здесь — рамка, взявшая дефолт, получила бы масштаб дважды.
    ...(frameStroke ? { frameStroke } : {}),
  };

  const binaryRefs = collectBinaryRefs(boxes);
  return { sceneJson: JSON.stringify(scene), imageRefs, ...(binaryRefs.length ? { binaryRefs } : {}) };
}

function collectLockedRenderIds(boxes: readonly RenderBox[]): ReadonlySet<string> {
  const ids = new Set<string>();
  const walkIds = (list: readonly RenderBox[]): void => {
    for (const b of list) {
      if (b.locked) ids.add(b.id);
      if (b.type === 'group') walkIds(b.children);
    }
  };

  walkIds(boxes);
  return ids;
}

type ImageLadderPick = Pick<SceneImageRef, 'thumbUrl' | 'placeholderUrl' | 'blocking' | 'imageId' | 'rungPx' | 'preservePngRgba'>;

// Backend-generated book models carry no `imageAlbum.id`, so photo identity comes from the address: every
// rendition of one photo lives in one uuid folder. A `blob:`/`data:` url has no folder — no identity from it,
// and the root folder ("/x.jpg") is nobody's identity either.
function photoFolderId(url: string | undefined): string | undefined {
  if (!url || !/^https?:\/\//i.test(url)) return undefined;
  // Схему регулярка пропускает, а хоста может не быть вовсе («http://»): `new URL` тогда бросает, и это
  // исключение уронило бы всю синхронную сборку сцены (`collectImageRefs`/`toSpreadScene`) ради identity.
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    return undefined;
  }

  const cut = path.lastIndexOf('/');
  return cut > 0 ? path.slice(0, cut) : undefined;
}

// Without an id a ref gets no identity, and a zoom lift is never planned for it — hence the folder fallback.
function albumImageId(b: RenderImageBox): string | undefined {
  const album = b.imageAlbum;
  if (!album) return undefined;
  return album.id ?? photoFolderId(album.thumb ?? album.small ?? album.medium_1280 ?? album.big_1920 ?? album.image);
}

function rungPxOf(album: RenderImageBox['imageAlbum'], url: string): number | undefined {
  if (!album) return undefined;
  for (const rung of RENDITION_RUNGS) {
    if (album[rung.field] === url) return rung.px;
  }

  return undefined;
}

// Progressive tiers: cheaper renditions of the SAME box image, painted while the target still downloads.
function imageLadder(box: RenderBox, target: string): ImageLadderPick {
  if (box.layerStyleRendering === 'live') return { blocking: true, preservePngRgba: true };
  if (box.type === 'object' || box.type === 'background') {
    const b = box as RenderObjectBox;
    if (b.pixelData && target === b.pixelData) return {};
    const thumb = b.versions?.thumb;
    const placeholder = thumbHashDataUrl(b.versions?.thumbHash);
    // Pack backgrounds are imported without renditions, so there is no cheap rung to paint first.
    const blocking = box.type === 'background' && !thumb && !placeholder;
    return {
      ...(thumb && thumb !== target ? { thumbUrl: thumb } : {}),
      ...(placeholder && placeholder !== target ? { placeholderUrl: placeholder } : {}),
      ...(blocking ? { blocking: true } : {}),
    };
  }

  if (box.type === 'smartObject') {
    const b = box as RenderSmartObjectBox;
    // The rasteriser applies the drawable's affine to whatever sits under the ref, and a drawable carries one
    // matrix — so any rung under a source-placed target (raw or rendition) would be drawn at the wrong scale.
    if (resolveSmartObjectPlacement(b).kind !== 'box') return {};
    const thumb = b.versions?.thumb;
    const placeholder = thumbHashDataUrl(b.thumbHash);
    return {
      ...(thumb && thumb !== target ? { thumbUrl: thumb } : {}),
      ...(placeholder && placeholder !== target ? { placeholderUrl: placeholder } : {}),
    };
  }

  if (box.type === 'image') {
    const b = box as RenderImageBox;
    const album = b.imageAlbum;
    const thumb = album?.thumb;
    const placeholder = thumbHashDataUrl(album?.thumb_hash) || thumbHashDataUrl(b.previewThumbHash);
    const imageId = albumImageId(b);
    const rungPx = rungPxOf(album, target);
    return {
      ...(thumb && thumb !== target ? { thumbUrl: thumb } : {}),
      ...(placeholder && placeholder !== target ? { placeholderUrl: placeholder } : {}),
      ...(imageId ? { imageId } : {}),
      ...(rungPx ? { rungPx } : {}),
    };
  }

  return {};
}

// Same refs (id+url, dedupe) as the TS toSpreadScene, not Rust build_scene: an undrawn box orders nothing itself, yet a box
// it cuts still orders it as a base. One extra on purpose: an unbaked clipper's pixel bake sources, ordered at the clipper's
// place, so a source walked later moves up; the order is otherwise the scene's, and no consumer depends on it.
// Pass its `renditionScale` as `scale` and, when `boxes` is only part of the spread, its `maskLayerBoxes`.
export function collectImageRefs(
  boxes: readonly RenderBox[],
  scale = 1,
  maskLayerBoxes: readonly RenderBox[] = boxes,
  // Preview bake only, as toSpreadScene's: a photo with no tier draws its original.
  originalFallback = false,
): SceneImageRef[] {
  const imageRefs: SceneImageRef[] = [];
  const liveClips = liveTextClipBases(boxes);
  const seen = new Set<string>();
  const byRef = new Map<string, SceneImageRef>();
  const addRef = (ref: string, url: string, ladder?: ImageLadderPick): void => {
    if (seen.has(ref)) {
      const existing = byRef.get(ref);
      if (existing && ladder?.preservePngRgba) existing.preservePngRgba = true;
      if (existing && ladder?.blocking) existing.blocking = true;
      return;
    }

    seen.add(ref);
    const entry: SceneImageRef = { ref, url, ...ladder };
    byRef.set(ref, entry);
    imageRefs.push(entry);
  };

  const maskRefOf = createMaskRefRegistry(addRef);
  // Каждый бокс разворота по id — рамке нужен слой, по которому её режут, даже если тот скрыт и обходом
  // не собирается: без байтов маски декодер оставил бы рамку целым прямоугольником.
  const byId = new Map<string, RenderBox>();
  const index = (bs: readonly RenderBox[]): void => {
    for (const b of bs) {
      byId.set(b.id, b);
      index((b as { children?: readonly RenderBox[] }).children ?? []);
    }
  };

  index(maskLayerBoxes);
  const lookup = (id: string): RenderBox | undefined => byId.get(id);
  const addBaseRef = (base: RenderObjectBox, url: string): void => addRef(base.id, url, imageLadder(base, url));
  const cuttingMaskRefOf = createCuttingMaskRef(maskRefOf, lookup, scale, addBaseRef);
  const clipMaskRefOf = (box: RenderBox): void => {
    const clip = clipMaskBaseOf(box, lookup, scale);
    if (clip) addBaseRef(clip.base, clip.url);
  };

  // The first bake draws these sources only from textures the canvas holds: a visible one repeats its own walk's ref
  // (deduped), a hidden one the walk skips.
  const bakeSourceRefsOf = (box: RenderBox): void => {
    for (const id of (box as RenderObjectBox).compoundClipBakeRecipe?.leafBoxIds ?? []) {
      const leaf = lookup(id);
      if (leaf?.type !== 'object' && leaf?.type !== 'background') continue;
      const url = objectBoxUrl(leaf as RenderObjectBox, scale);
      if (url) addBaseRef(leaf as RenderObjectBox, url);
    }
  };

  const addObjectRef = (box: RenderBox, url: string): void => {
    addRef(box.id, url, imageLadder(box, url));
    // Кадр ждёт ту же маску, которой его режет сцена: без неё он мелькнул бы целым прямоугольником.
    const maskRef = cuttingMaskRefOf(box);
    const entry = byRef.get(box.id);
    if (maskRef && entry) {
      entry.requiresRef = maskRef;
    }
  };

  const walk = (box: RenderBox): void => {
    if (box.visible === false || ((box.type === 'text' || box.layerStyleRendering === 'live') && box.opacity === 0)) return;
    if (box.type === 'adjustment' && adjustmentIsActive(box) && !box.adjustment.maskDisabled) {
      maskRefOf(Object.assign({}, box, { maskDataUrl: box.adjustment.maskDataUrl || box.adjustment.maskUrl }), BLOCKING_MASK);
    }

    styleMaskRefs(box, (ref, url) => addRef(ref, url, STYLE_RESOURCE));
    const pattern =
      (box.type === 'text' || box.layerStyleRendering === 'live') &&
      box.effects?.patternOverlay?.enabled !== false &&
      box.effects?.patternOverlay?.opacity !== 0
        ? box.effects?.patternOverlay?.pattern
        : undefined;
    const patternUrl = pattern?.url || pattern?.pixelData;
    if (patternUrl)
      addRef(`${box.id}__pattern`, patternUrl, box.layerStyleRendering === 'live' ? STYLE_RESOURCE : { blocking: true });
    // A shape's pattern fill: the Rust scene samples it as `<id>__fill_pattern` (adapter build_vector_fill).
    const fill = box.type === 'vector' ? box.vector?.fill : undefined;
    const fillPatternUrl = fill?.kind === 'pattern' ? fill.pattern?.url || fill.pattern?.pixelData : undefined;
    if (fillPatternUrl) addRef(`${box.id}__fill_pattern`, fillPatternUrl, STYLE_RESOURCE);
    const strokePaint = box.type === 'vector' ? box.vector?.stroke?.fill : undefined;
    const strokePattern = strokePaint?.kind === 'pattern' ? strokePaint.pattern : undefined;
    const strokePatternUrl = strokePattern?.url || strokePattern?.pixelData;
    if (strokePatternUrl) addRef(`${box.id}__stroke_fill_pattern`, strokePatternUrl, STYLE_RESOURCE);
    switch (box.type) {
      case 'background':
      case 'object': {
        if (isUnbakedCompoundClip(box) && !liveClips.has(box.id)) {
          bakeSourceRefsOf(box);
          break;
        }

        const url = objectBoxUrl(box, scale);
        if (!url) break;
        addObjectRef(box, url);
        // Слот клип-группы, пока он ещё декор, режется слоем-маской так же, как рамка (`clipRef ?? maskRefOf`
        // в сцене): без байтов этого слоя скрытая маска оставила бы слот целым прямоугольником.
        clipMaskRefOf(box);
        break;
      }

      case 'smartObject': {
        const { url } = resolveSmartObjectPlacement(box);
        if (url) addObjectRef(box, url);
        break;
      }

      case 'vector': {
        if (buildVectorDrawable(box as RenderVectorBox, 0, 0)) break;
        const url = vectorUrl(box);
        if (url) addObjectRef(box, url);
        break;
      }

      case 'image': {
        const url = imageBoxUrl(box, scale, originalFallback);
        // A photo-less frame draws as the placeholder shape, whose mask is then its ONLY asset — skipping
        // it left the decoder without the bytes and the mask silently never appeared.
        if (url) addObjectRef(box, url);
        else maskRefOf(box);
        clipMaskRefOf(box);
        const frame = frameOf(box);
        if (frame) addRef(`${box.id}__frame`, frame.url);
        break;
      }

      case 'group': {
        // A group node's mask reaches the engine as `mask:<ref>`, the same key the adapter emits.
        const groupMask = (box as RenderGroupBox).psdGroup?.mask?.ref;
        if (groupMask) maskRefOf({ maskDataUrl: groupMask } as unknown as RenderBox, BLOCKING_MASK);
        for (const child of (box as RenderGroupBox).children) walk(child);
        break;
      }

      default:
        break;
    }
  };

  for (const box of boxes) {
    if (box.visible === false) continue;
    walk(box);
  }

  return imageRefs;
}

// Zones are browser-only editor chrome that build_scene must not emit, so the host layers them onto the built scene.
export function appendZoneShapes(
  sceneJson: string,
  scale: number,
  zones?: ZonePrintSizes,
  zoneVisibility?: ZoneVisibility,
  zoneStyle?: ZoneStyle,
): string {
  if (!zones) return sceneJson;
  const scene = JSON.parse(sceneJson) as { viewport: unknown; drawables: RustDrawable[] };
  const shapes = buildZoneShapes(zones, zoneVisibility, zoneStyle);
  scene.drawables.push(...(scale === 1 ? shapes : shapes.map(s => scaleRustDrawable(s, scale))));
  return JSON.stringify(scene);
}

function scaleRect(r: RustRect, s: number): RustRect {
  return { x: r.x * s, y: r.y * s, width: r.width * s, height: r.height * s, rotation: r.rotation };
}

function scaleMaskFrame(r: RustMaskRect, s: number): RustMaskRect {
  return { rotation: 0, ...scaleMaskRect(r, s, s) };
}

function scaleRustDrawable(d: RustDrawable, s: number): RustDrawable {
  d = { ...d, ...transformEffectOrigins(d, [s, 0, 0, s, 0, 0]) };
  if (d.layerStyles || d.layerStyleMasks) {
    const fx = d.layerStyles;
    const masks: SceneStyleFields['layerStyleMasks'] = {};
    for (const stage of ['source', 'post'] as const) {
      if (d.layerStyleMasks?.[stage])
        masks[stage] = d.layerStyleMasks[stage].map(mask => ({
          ...mask,
          transform: [...multiplyAffine([s, 0, 0, s, 0, 0], mask.transform)],
        }));
    }

    d = {
      ...d,
      ...(d.layerStyleMasks && { layerStyleMasks: masks }),
      ...(fx && {
        layerStyles: {
          ...fx,
          shadows: fx.shadows?.map(sh => scaleShadow(sh, s)),
          innerShadows: fx.innerShadows?.map(sh => scaleShadow(sh, s)),
          strokes: fx.strokes?.map(st => ({
            ...transformStrokeGradient(st, [s, 0, 0, s, 0, 0]),
            width: st.width * s,
            ...(st.fillType === 'pattern' && {
              patternScale: (st.patternScale ?? 1) * s,
              patternPhaseX: (st.patternPhaseX ?? 0) * s,
              patternPhaseY: (st.patternPhaseY ?? 0) * s,
            }),
          })),
          layerGlows: fx.layerGlows?.map(g => ({ ...g, blur: g.blur * s })),
          satin: fx.satin
            ? {
                ...fx.satin,
                blur: fx.satin.blur * s,
                offsetX: fx.satin.offsetX * s,
                offsetY: fx.satin.offsetY * s,
                distance: fx.satin.distance == null ? undefined : fx.satin.distance * s,
              }
            : undefined,
          bevel: fx.bevel
            ? {
                ...fx.bevel,
                size: fx.bevel.size * s,
                texture: fx.bevel.texture
                  ? {
                      ...fx.bevel.texture,
                      scale: (fx.bevel.texture.scale ?? 1) * s,
                      phaseX: (fx.bevel.texture.phaseX ?? 0) * s,
                      phaseY: (fx.bevel.texture.phaseY ?? 0) * s,
                    }
                  : undefined,
                soften: fx.bevel.soften == null ? undefined : fx.bevel.soften * s,
              }
            : undefined,
          gradientOverlay: fx.gradientOverlay ? transformGradientOverlay(fx.gradientOverlay, [s, 0, 0, s, 0, 0]) : undefined,
          ...(fx.extraGradientOverlays && {
            extraGradientOverlays: fx.extraGradientOverlays.map(g => transformGradientOverlay(g, [s, 0, 0, s, 0, 0])),
          }),
          patternOverlay: fx.patternOverlay
            ? {
                ...fx.patternOverlay,
                scale: (fx.patternOverlay.scale ?? 1) * s,
                phaseX: (fx.patternOverlay.phaseX ?? 0) * s,
                phaseY: (fx.patternOverlay.phaseY ?? 0) * s,
              }
            : undefined,
        },
      }),
    };
  }

  // Stroke width/dash scale with the scene too, so on-screen they match the editor's BleedSafeZoneLayer.
  if (d.type === 'shape') {
    return {
      ...d,
      rect: scaleRect(d.rect, s),
      // Маска живёт в СВОЁМ месте разворота, а не в рамке: её прямоугольник — такая же геометрия сцены,
      // и без этого масштаба она осталась бы в книжных px, пока кадр уехал в рендерные.
      ...(d.maskRect ? { maskRect: scaleMaskFrame(d.maskRect, s) } : {}),
      strokeWidth: d.strokeWidth != null ? d.strokeWidth * s : d.strokeWidth,
      dash: d.dash ? d.dash.map(v => v * s) : d.dash,
      ...(d.decor ? { decor: scaleFrameDecor(d.decor, s) } : {}),
    };
  }

  if (d.type === 'object')
    return {
      ...d,
      rect: scaleRect(d.rect, s),
      ...(d.maskRect ? { maskRect: scaleMaskFrame(d.maskRect, s) } : {}),
      ...(d.decor ? { decor: scaleFrameDecor(d.decor, s) } : {}),
      contentRect: d.contentRect ? scaleRect(d.contentRect, s) : undefined,
      sourceTransform: d.sourceTransform
        ? (d.sourceTransform.map(v => v * s) as [number, number, number, number, number, number])
        : undefined,
      ...(d.smartFilters
        ? { smartFilters: d.smartFilters.map(f => (f.kind === 'gaussianBlur' ? { ...f, radius: f.radius * s } : f)) }
        : {}),
    };
  if (d.type === 'vector') return scaleSceneVector(d, s);
  if (d.type === 'adjustment')
    return {
      ...d,
      rect: d.rect ? scaleRect(d.rect, s) : undefined,
      maskRect: d.maskRect
        ? {
            x: d.maskRect.x * s,
            y: d.maskRect.y * s,
            width: d.maskRect.width * s,
            height: d.maskRect.height * s,
          }
        : undefined,
      maskFeather: d.maskFeather == null ? undefined : d.maskFeather * s,
      maskVector: d.maskVector
        ? {
            ...d.maskVector,
            paths: scalePaths(d.maskVector.paths, s, s, 0, 0),
            feather: d.maskVector.feather == null ? undefined : d.maskVector.feather * s,
          }
        : undefined,
    };
  if (d.type === 'image') {
    return {
      ...d,
      rect: scaleRect(d.rect, s),
      imageRect: d.imageRect ? scaleRect(d.imageRect, s) : undefined,
      ...(d.maskRect ? { maskRect: scaleMaskFrame(d.maskRect, s) } : {}),
      clip: d.clip ? d.clip.map(k => ({ ...k, points: k.points.map(n => n * s) })) : undefined,
      strokeWidth: d.strokeWidth != null ? d.strokeWidth * s : d.strokeWidth,
      ...(d.decor ? { decor: scaleFrameDecor(d.decor, s) } : {}),
    };
  }

  return {
    ...d,
    rect: { ...d.rect, x: d.rect.x * s, y: d.rect.y * s, width: d.rect.width * s, height: d.rect.height * s },
    inkRect: d.inkRect
      ? { x: d.inkRect.x * s, y: d.inkRect.y * s, width: d.inkRect.width * s, height: d.inkRect.height * s }
      : undefined,
    glyphs: d.glyphs.map(g => ({ ...g, x: g.x * s, y: g.y * s, fontSize: g.fontSize * s })),
    fauxBoldWidth: d.fauxBoldWidth == null ? undefined : d.fauxBoldWidth * s,
    decorations: d.decorations.map(dec => ({ ...dec, x: dec.x * s, y: dec.y * s, width: dec.width * s, height: dec.height * s })),
    strokes: d.strokes.map(st => ({
      ...transformStrokeGradient(st, [s, 0, 0, s, 0, 0]),
      width: st.width * s,
      ...(st.fillType === 'pattern' && {
        patternScale: (st.patternScale ?? 1) * s,
        patternPhaseX: (st.patternPhaseX ?? 0) * s,
        patternPhaseY: (st.patternPhaseY ?? 0) * s,
      }),
    })),
    shadows: d.shadows?.map(sh => scaleShadow(sh, s)),
    innerShadows: d.innerShadows?.map(sh => scaleShadow(sh, s)),
    glows: d.glows?.map(sh => scaleShadow(sh, s)),
    layerGlows: d.layerGlows?.map(g => ({ ...g, blur: g.blur * s })),
    satin: d.satin
      ? {
          ...d.satin,
          blur: d.satin.blur * s,
          offsetX: d.satin.offsetX * s,
          offsetY: d.satin.offsetY * s,
          distance: d.satin.distance == null ? undefined : d.satin.distance * s,
        }
      : undefined,
    patternOverlay: d.patternOverlay
      ? {
          ...d.patternOverlay,
          scale: (d.patternOverlay.scale ?? 1) * s,
          phaseX: (d.patternOverlay.phaseX ?? 0) * s,
          phaseY: (d.patternOverlay.phaseY ?? 0) * s,
        }
      : undefined,
    bevel: d.bevel
      ? {
          ...d.bevel,
          size: d.bevel.size * s,
          soften: d.bevel.soften == null ? undefined : d.bevel.soften * s,
          texture: d.bevel.texture
            ? {
                ...d.bevel.texture,
                scale: (d.bevel.texture.scale ?? 1) * s,
                phaseX: (d.bevel.texture.phaseX ?? 0) * s,
                phaseY: (d.bevel.texture.phaseY ?? 0) * s,
              }
            : undefined,
        }
      : undefined,
    transform: d.transform ? scaleAffineTranslation(d.transform as Affine, s) : undefined,
    fillGradient: scaleFill(d.fillGradient, s),
    gradientOverlay: d.gradientOverlay ? transformGradientOverlay(d.gradientOverlay, [s, 0, 0, s, 0, 0]) : undefined,
    ...(d.extraGradientOverlays && {
      extraGradientOverlays: d.extraGradientOverlays.map(g => transformGradientOverlay(g, [s, 0, 0, s, 0, 0])),
    }),
  };
}
